import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiTrendingDown, FiMinus } = FiIcons;

const SentimentAnalysis = ({ newsData, sentimentAnalysis }) => {
  const sentimentData = [
    { name: 'Positive', value: sentimentAnalysis.counts.positive, color: '#10b981' },
    { name: 'Negative', value: sentimentAnalysis.counts.negative, color: '#ef4444' },
    { name: 'Neutral', value: sentimentAnalysis.counts.neutral, color: '#6b7280' }
  ];

  // Group news by type for impact analysis
  const typeImpact = newsData.reduce((acc, news) => {
    if (!acc[news.type]) {
      acc[news.type] = { total: 0, count: 0 };
    }
    acc[news.type].total += Math.abs(news.impact);
    acc[news.type].count += 1;
    return acc;
  }, {});

  const impactData = Object.entries(typeImpact).map(([type, data]) => ({
    type: type.charAt(0).toUpperCase() + type.slice(1),
    impact: ((data.total / data.count) * 100).toFixed(1),
    count: data.count
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold">{payload[0].name}</p>
          <p className="text-sm">Count: {payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  const BarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold">{label}</p>
          <p className="text-sm">Avg Impact: {payload[0].value}%</p>
          <p className="text-sm">Articles: {payload[0].payload.count}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Sentiment Distribution */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Sentiment Distribution</h3>
        
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={sentimentData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {sentimentData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        <div className="mt-4 space-y-2">
          {sentimentData.map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2" 
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-sm">{item.name}</span>
              </div>
              <span className="text-sm font-medium">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* News Type Impact */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Impact by News Type</h3>
        
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={impactData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="type" stroke="#666" />
              <YAxis stroke="#666" />
              <Tooltip content={<BarTooltip />} />
              <Bar dataKey="impact" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="mt-4 text-sm text-gray-600">
          <p>Average absolute impact by news category</p>
        </div>
      </div>

      {/* Recent High-Impact News */}
      <div className="bg-white rounded-xl shadow-lg p-6 lg:col-span-2">
        <h3 className="text-xl font-bold text-gray-800 mb-4">High-Impact News (Last 7 Days)</h3>
        
        <div className="space-y-3">
          {newsData
            .filter(news => {
              const newsDate = new Date(news.date);
              const cutoff = new Date();
              cutoff.setDate(cutoff.getDate() - 7);
              return newsDate >= cutoff && Math.abs(news.impact) > 0.05;
            })
            .slice(0, 5)
            .map((news, index) => (
              <div key={news.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className={`p-2 rounded-full ${
                  news.sentiment === 'positive' ? 'bg-green-100' : 
                  news.sentiment === 'negative' ? 'bg-red-100' : 'bg-gray-100'
                }`}>
                  <SafeIcon 
                    icon={news.sentiment === 'positive' ? FiTrendingUp : 
                          news.sentiment === 'negative' ? FiTrendingDown : FiMinus} 
                    className={`w-4 h-4 ${
                      news.sentiment === 'positive' ? 'text-green-600' : 
                      news.sentiment === 'negative' ? 'text-red-600' : 'text-gray-600'
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{news.headline}</p>
                  <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                    <span>{news.source}</span>
                    <span>•</span>
                    <span>{news.date}</span>
                    <span>•</span>
                    <span className={`font-medium ${
                      news.impact > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {news.impact > 0 ? '+' : ''}{(news.impact * 100).toFixed(1)}% impact
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default SentimentAnalysis;